import React from "react";
import CloudFile404 from "../CloudFile404";
import ShareScene from "./ShareScene";
import MadeWithBadge from "./MadeWithBadge";
import { useShareFile } from "./useShareFile";
import { getShareFileIdFromPath } from "../../data/shareRouting";

/**
 * Chrome-less presentation of `/share/<id>` for iframe/embed contexts (Notion,
 * Confluence, blogs). Same data path (`useShareFile`) and same inert canvas
 * (`ShareScene`) as the standalone {@link ShareView}, but with ZERO chrome:
 *
 *   - no title bar, no "Create your own" link, no owner Edit, no share popover —
 *     the surface renders NO editing or authenticated affordances regardless of
 *     who is viewing (the owner included). Read-only is structural, not hidden.
 *   - the canvas fits-to-content on load so a mindmap lands framed in a small
 *     iframe, with pan/zoom still enabled.
 *   - a single "Made with Mindmap.so" badge (opens the app in a new tab).
 *
 * A private / deleted / nonexistent file collapses to the same uniform 404,
 * which renders politely at iframe sizes (centered, height:100%).
 */
export const EmbedView: React.FC = () => {
  const fileId = getShareFileIdFromPath();
  const { status, file, scene } = useShareFile(fileId);

  if (status === "loading") {
    return (
      <div
        data-testid="embed-view-loading"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
        }}
      >
        Loading…
      </div>
    );
  }

  if (status === "notfound" || !file || !scene) {
    return <CloudFile404 />;
  }

  return (
    <div
      data-testid="embed-view"
      style={{ position: "relative", height: "100%", width: "100%" }}
    >
      <ShareScene
        fileId={file.id}
        scene={scene}
        fitToContentOnLoad
        chromeless
      />
      <MadeWithBadge />
    </div>
  );
};

export default EmbedView;
